<template>
	<b-container fluid class="d-flex flex-column mh-100 h-100 w-100 no-gutters p-0">
		<b-row v-if="ShowSearchButton || ShowToolbar" class="pt-1 w-100 mx-2">
			<b-col v-if="ShowSearchButton" class="p-0">
				<b-button-toolbar class="mb-1">
					<b-button-group>
						<!-- Barre d'outils avant le bouton Search -->
						<slot name="ToolBarBeforeSearch" />

						<!-- bouton de recherche de patrimoine -->
						<!-- HeritageSearch -> Recherche patrimoine -->
						<b-button @click="searchVisible = true" :pressed="searchVisible" :disabled="Disable" v-b-tooltip.noninteractive.hover :title="$t('HeritageSearch')"  variant="outline-dark"
							class="btn-circle border-0 p-0">
							<b-icon icon="search" />
						</b-button>

						<!-- Barre d'outils après le bouton Search -->
						<slot name="ToolBarAfterSearch" />
					</b-button-group>
				</b-button-toolbar>
				<!-- Composant de recherche de patrimoine -->
				<Search v-if="searchVisible" :UseGrouping="true" :Visible.sync="searchVisible" :Variant="Variant" :FilterTreeStore="FilterTreeStore" />
			</b-col>
			<b-col v-if="ShowToolbar">
				<b-button-toolbar :class="['mb-1', {'float-right ml-auto': ShowSearchButton } ]">
					<b-overlay :show="Disable" :opacity="0.2" variant="gray-light" >
						<template #overlay>
							<div class="m-0 pt-1 h-100 text-center d-flex align-items-center justify-content-center"/>
						</template>
						<b-button-group>
							<!-- Affiche ou masque les noeuds dépréciés -->
							<!-- $t('HideHistoric') / Masquer l'historique -->
							<!-- $t('ShowHistoric') / Afficher l'historique -->
							<b-button v-if="hasHistoricMode" @click="$store.dispatch(FilterTreeStore + '/ToggleHistoricMode')" :disabled="Disable" v-b-tooltip.noninteractive.hover.top :title="isHistoricMode ? $t('HideHistoric') : $t('ShowHistoric')" variant="outline-dark"
								:pressed.sync="isHistoricMode"
								class="btn-circle border-0 depreciateBtn">
								<!-- <font-awesome-icon icon="clock-rotate-left" /> -->
								<b-icon icon="clock-history" />
							</b-button>

							<!-- Bouton pour Gérer les filtres par propriété de type objet -->
							<!-- $t('LabelByObjectType') / Etiquette par propriétés de type d'objet -->
							<b-button v-if="hasPropertiesFilter" @click="$store.dispatch(FilterTreeStore + '/SetPropertiesFilterVisible', true)" :disabled="Disable" v-b-tooltip.noninteractive.hover.topright :title="$t('LabelByObjectType')" variant="outline-dark" class="btn-circle border-0" :text="$t('Filters')">
								<b-icon icon="tags"/>
							</b-button>

							<!-- $t('ShowTreeStructure') / Afficher l'arborescence complète -->
							<!-- $t('ShowSelection') / Afficher la sélection -->
							<b-button v-if="hasToggleCheckOnly" @click="toggleCheckOnly()" :disabled="Disable" v-b-tooltip.noninteractive.hover.top :title="isCheckOnly ? $t('ShowTreeStructure') : $t('ShowSelection')" variant="outline-dark" 
								:pressed.sync="isCheckOnly"
								class="btn-circle border-0" >
								<b-icon icon="list-nested" />
							</b-button>
							<!-- $t('SelectAll') / Tout sélectionner -->
							<b-button v-if="hasSelectAll" @click="selectAllNodes()" :disabled="Disable" v-b-tooltip.noninteractive.hover.top :title="$t('SelectAll')" variant="outline-dark" 
								class="btn-circle border-0">
								<b-icon icon="check2-all" />
							</b-button>
							<!-- $t('ClearSelection') / Effacer la sélection -->
							<b-button v-if="hasClearSelection" @click="removeAllPins()" :disabled="Disable" v-b-tooltip.noninteractive.hover.top :title="$t('ClearSelection')" variant="outline-dark"
								class="btn-circle border-0">
								<b-icon icon="eraser" />
							</b-button>
						</b-button-group>
					</b-overlay>
				</b-button-toolbar>
			</b-col>
		</b-row>
		<b-row class="flex-grow-1 w-100 overflow-auto no-gutters p-0 m-0">
			<div class="HeritageContainer h-auto w-100 p-0 m-0" :style="generateVariantStyle(Variant)">
				<b-overlay :show="Disable" :opacity="0.2" variant="gray-light" >
					<template #overlay>
						<div class="m-0 pt-1 h-100 text-center d-flex align-items-center justify-content-center"/>
					</template>
					<!-- HeritagesNodes -->
					<b-container v-if="getHeritageTreeAlive" fluid class="p-0 m-0">
						<!-- Créé la liste des noeuds enfant de premier niveau -->
						<!-- v-on="$listeners" utilisé pour passer les events par v-model -->
						<heritage-node v-for="childNode in selectedNodes" :key="childNode.Id" :node="childNode" :FilterTreeStore="FilterTreeStore" v-on="$listeners" />
					</b-container>
				</b-overlay>
			</div>
		</b-row>
	</b-container>
</template>

<script lang="ts">
	import Component, { mixins } from 'vue-class-component';
	import { Prop, ProvideReactive, Vue } from 'vue-property-decorator';
	import HeritageNode from './HeritageTree/HeritageNode.vue';
	import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
	import TreeNode from '@/types/TreeNode';
	import { ThemeUtils } from './Utils';
	import Search from "@/components/commons/Search.vue";
	import locales from './locales/HeritageTree.json';
	import { FilterTreeBuilder } from './FilterTreeBuilder';
	import IConfigTree from '@/interfaces/IConfigTree';
	import SelectModeEnum from '@/enums/SelectModeEnum';

	/** Composant affichant l'arborescence */
	@Component({
		name: 'heritage-tree',
		components: {
			HeritageNode,
			FontAwesomeIcon,
			Search
		},
		i18n: {messages: locales}
	})
	export default class HeritageTree extends ThemeUtils {
		/**
		 * Indique si l'intéractivité de l'aborescence doit être désactivé
		 * @default false
		 */
		@Prop({ default: false })
		public Disable!: boolean;

		/** 
		 * Variant de couleur à appliquer sur le composant
		 * @default secondary
		 */
		@Prop({ default: 'secondary' })
		@ProvideReactive()
		public Variant!: string;

		/** Indique l'uid de l'HeritageTree courant à tous les HeritageNode pour distinguer les instances d'arborescence */
		@ProvideReactive()
		private HeritageTreeUid: string = this.$id('HeritageTree');

		/** Nom du portal où afficher la barre d'outil */
		@Prop({ default: false })
		public ShowToolbar!: boolean;

		/** Indique si le bouton de recherche de patrimoine est affiché ou non */
		@Prop({ default: false })
		public ShowSearchButton!: boolean;

		/** Détermine si la modal de recherche s'affiche ou non*/
		private searchVisible: boolean = false;

		/** Nom du store Vuex persistant l'arborescence */
		@Prop()
		public FilterTreeStore!: string;

		/** Noeud constituant le premier noeud de l'arborescence */
		@Prop()
		public RootNode!: TreeNode;

		//#region Création instance d'arborescence
		private filterTreeBuilder!: FilterTreeBuilder;

		/** Clone une arborescence existante */
		public async Clone(sourceFilterTree: string = undefined, pins = undefined, configTree: IConfigTree = undefined, selectMode: SelectModeEnum = undefined): Promise<any> {
			this.filterTreeBuilder = new FilterTreeBuilder(this.$store, this.FilterTreeStore);
			await this.filterTreeBuilder.Clone(sourceFilterTree, pins, configTree, selectMode);
		}

		/** Initialisation d'une nouvelle instance d'arborescence **/
		public async Load(isUserPerimeter: boolean = true, pins = undefined, selectMode: SelectModeEnum = undefined, configTree: IConfigTree = undefined): Promise<any> {
			this.filterTreeBuilder = new FilterTreeBuilder(this.$store, this.FilterTreeStore);
			await this.filterTreeBuilder.Load(isUserPerimeter, pins, selectMode, configTree);
		}

		//#endregion Création instance d'arborescence

		/** TODO FFORT : Cela ne va pas, car je souhaite que seulement les rafraischissement de l'arbo soit pris en compte 
		 * Il serait bien que ce soit fais aprés le reset de l'arborescence
		*/
		private get getHeritageTreeAlive(): boolean {
			return this.$store.getters[this.FilterTreeStore + '/GetHeritageTreeAlive'];
		}

		/** Liste des noeuds enfants */
        private get selectedNodes(): TreeNode[] {
			/** Si un noeud root est précisé par le composant appelant alors on le prend comme premier noeud */
			if (this.RootNode)
				return this.$store.getters[this.FilterTreeStore + '/ChildNodes'](this.RootNode);
			else
            	return this.$store.getters[this.FilterTreeStore + '/SelectedNodes'];
		}

        /** Indique si les noeuds de l'arborescence peuvent être drag&droper */
        @Prop({ default: () => false })
		@ProvideReactive()
        public IsDraggable!: boolean;

		/** Fonction associée au bouton "Afficher la sélection"
	 	* Affichage uniquement des noeuds sélectionnés */
		private toggleCheckOnly() {
			this.$store.dispatch(this.FilterTreeStore + "/ToggleCheckOnly")
			.catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / Une erreur s'est produite.
					title: this.$t('Error').toString(), // $t('Error') / Erreur
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			});
		}

		/** Fonction associée au bouton "Effacer la sélection"
		 * Suppression de toutes les sélections*/
		private removeAllPins() {
			this.$store.dispatch(this.FilterTreeStore + "/RemoveAllPins")
			.catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / Une erreur s'est produite.
					title: this.$t('Error').toString(), // $t('Error') / Erreur
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			});
		}

		/** Fonction associée au bouton "Tout sélectionné"
		 * Sélectionne tout les noeuds de premier niveau*/
		 private selectAllNodes() {
			this.$store.dispatch(this.FilterTreeStore + "/SelectAllNodes")
			.catch(err => {
				console.error(err);
				this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // $t('AnErrorOccured') / Une erreur s'est produite.
					title: this.$t('Error').toString(), // $t('Error') / Erreur
					autoHideDelay: 10000,
					variant: "danger",
					solid: true,
					appendToast: true,
					toaster: "b-toaster-top-center"
				});
			});
		}

		/** Détermine si le mode Historique est actif ou non */
		private isCheckOnly: boolean = false;
		
		/** Détermine si le mode Historique est actif ou non */
		private isHistoricMode: boolean = false;

		/** Détermine si le bouton Historique est visible ou non */
		private hasHistoricMode: boolean = false;

		/** Affichage si bouton Filtre par propriété est visible ou non */
		private hasPropertiesFilter: boolean = false;

		/** Détermine si le bouton affiché que les éléments sélectionnés est visible ou non */
		private hasToggleCheckOnly: boolean = false;

		/** Détermine si le bouton sélectionné tout est visible ou non */
		private hasSelectAll: boolean = false;

		/** Détermine si le bouton désélectionné tout est visible ou non */
		private hasClearSelection: boolean = false;

		//#region Unwatchers pour libérer de la mémoire
		/** 
         * Pointeurs vers fonction pour désactiver watchers
         * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher
         **/
		private unwatchHasHistoricMode = undefined;
		private unwatchHasPropertiesFilter = undefined;
		private unwatchHasToggleCheckOnly = undefined;
		private unwatchHasSelectAll = undefined;
		private unwatchHasClearSelection = undefined;
		private unwatchGetHistoricMode = undefined;
		private unwatchIsCheckOnly = undefined;
		private unsubscribeOpenNode = undefined;
		//#endregion Unwatchers pour libérer de la mémoire

		created() {
			/** Rafraichissement si le bouton mode historique est affiché ou non */
			this.unwatchHasHistoricMode = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/HasHistoricMode'], val => {
				this.hasHistoricMode = val;
			}, { immediate: true });

			/** Rafraichissement si le bouton de filtre par propriété est affiché ou non */
			this.unwatchHasPropertiesFilter = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/HasPropertiesFilter'], val => {
				this.hasPropertiesFilter = val;
			}, { immediate: true });

			/** Rafraichissement si le bouton mode historique est affiché ou non */
			this.unwatchHasToggleCheckOnly = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/HasToggleCheckOnly'], val => {
				this.hasToggleCheckOnly = val;
			}, { immediate: true });

			/** Rafraichissement si le bouton sélectionné tout est visible ou non */
			this.unwatchHasSelectAll = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/HasSelectAll'], val => {
				this.hasSelectAll = val;
			}, { immediate: true });

			/** Rafraichissement si le bouton désélectionné tout est visible ou non */
			this.unwatchHasClearSelection = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/HasClearSelection'], val => {
				this.hasClearSelection = val;
			}, { immediate: true });

			/** Rafraichissement de filtres sur changement du mode historique
			 * @todo à mettre plutôt directement dans FilterTree ?
			 */
			this.unwatchGetHistoricMode = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/GetHistoricMode'], val => {
				this.isHistoricMode = val;
			}, { immediate: true });

			/** Mise à jour des noeuds sur changement du mode IsCheckOnly
			 * @todo à mettre plutôt directement dans FilterTree ?
			 */
			this.unwatchIsCheckOnly = this.$store.watch((state, getters) => getters[this.FilterTreeStore + '/IsCheckOnly'], val => {
				this.isCheckOnly = val;
			}, { immediate: true });

			/** Si une mutation de type openNode est déclenchée alors on scroll sur l'élement ouvert 
			 * @todo Est-ce bien le plus performant à faire de scruter les mutations... ?🤔  */
			this.unsubscribeOpenNode = this.$store.subscribe((mutation, state) => {
				if(mutation.type === this.FilterTreeStore + '/openNode' && mutation.payload.scrollIntoView){
					console.debug("HeritageTree - created - mutation : ", mutation);
					
					/** Vue.nextTick utilisé pour attendre que la mise à jour du DOM soit réalisé et que l'arbre soit bien ouvert sur le noeud à scroller */
					Vue.nextTick(() => {
						Vue.nextTick(() => {
							let nodeId: string = mutation.payload.node?.Id;
							const el = document.getElementById(this.HeritageTreeUid + '-HeritageNode' + nodeId);
							if (el)
								el.scrollIntoView(true);
						});
					});
				}
			})
		}

		beforeDestroy() {
			if (this.filterTreeBuilder)
				this.filterTreeBuilder.UnloadFilterTree();

			/** Libération de la mémoire occupée par les watchers */
			if (this.unwatchHasHistoricMode)
				this.unwatchHasHistoricMode();
			if (this.unwatchHasPropertiesFilter)
				this.unwatchHasPropertiesFilter();
			if (this.unwatchHasToggleCheckOnly)
				this.unwatchHasToggleCheckOnly();
			if (this.unwatchHasSelectAll)
				this.unwatchHasSelectAll();
			if (this.unwatchHasClearSelection)
				this.unwatchHasClearSelection();
			if (this.unsubscribeOpenNode)
				this.unsubscribeOpenNode();
			if (this.unwatchGetHistoricMode)
				this.unwatchGetHistoricMode();
			if (this.unwatchIsCheckOnly)
				this.unwatchIsCheckOnly();
		}
	}
</script>

<style scoped>
	.HeritageContainer {
		position: static;
		box-sizing: border-box;
		z-index: 9;
		background-color: transparent;
	}

	.depreciateBtn.active {
		color: red !important;
		background-color: transparent !important;
	}
</style>